import * as React from 'react';
import {
    QuestionFormBase,
    QuestionFormBaseProps,
    QuestionFormBaseState
} from '../QuestionFormBase';
import {
    MULTIMEDIA_TYPES,
    OptionData,
    QTYPES,
    TRADE_OFF_TYPES
} from '@nexxt/common/types';
import { getNewOption } from '../../common/buildOptions';
import { IOptionPack } from '../../../../libraries/optionpack/OptionPack';
import { Option } from '../../common/optionCard';
import { IOptionRederSettings } from '../../../../../interface/optionSettings.interface';
import { OPTION_TYPES, Question, Condition } from '@nexxt/common/types';
import { defaultRules } from '../../ConditionEditor/default';
import { OTHER_OPTION_VALUE } from '@nexxt/common/constants';
export type QuestionFormWithOptionsBaseProps = QuestionFormBaseProps;

export interface QuestionFormWithOptionsBaseStates
    extends QuestionFormBaseState {
    isOptionSettingsOpen: boolean;
    isPipeOptionModalOpen: boolean;
    isOptionPackModalOpen: boolean;
    openOpenPack: boolean;
    showCardSettings: boolean;
    optionSettingErrors: {
        number: string;
    };
    enableAdd: boolean;
    isOptionExpand: boolean;
}

export class QuestionFormWithOptionsBase<
    P extends QuestionFormWithOptionsBaseProps,
    S extends QuestionFormWithOptionsBaseStates
> extends QuestionFormBase<P, S> {
    constructor(p: Readonly<P>, types: QTYPES) {
        super(p, types);
        this.state = {
            ...(this.state as QuestionFormWithOptionsBaseStates),
            isOptionSettingsOpen: false,
            optionSettingErrors: { number: '' },
            isPipeOptionModalOpen: false,
            isOptionPackModalOpen: false,
            openOpenPack: false,
            showCardSettings:
                this.state.question?.options?.length > 0 ? true : false, // change this to another condition
            enableAdd: true,
            isOptionExpand: false
        } as S;
    }

    /**
     * Toggle Option Setting Box Show and Hide.
     */
    toggleOptionSettings = () => {
        this.setState({
            isOptionSettingsOpen: !this.state.isOptionSettingsOpen
        });
    };

    /**
     * Toggle Option Setting Box Show and Hide.
     */
    togglePipeOptionModal = () => {
        this.setState({
            isPipeOptionModalOpen: !this.state.isPipeOptionModalOpen
        });
    };

    /**
     * Toggle Option pack box show and hide
     */
    toggleOptionPackModal = () => {
        if (this.state.isOptionPackModalOpen) {
            this.setState({
                isOptionPackModalOpen: false
            });
        } else {
            if (
                this.state.question.options?.length == 0 &&
                ![QTYPES.POPUP].includes(this.state.question.type)
            ) {
                this.setState({
                    isOptionSettingsOpen: true,
                    openOpenPack: true
                });
            } else {
                this.setState({
                    isOptionPackModalOpen: true
                });
            }
        }
    };

    toggleOptionExpand = () => {
        this.setState({
            isOptionExpand: !this.state.isOptionExpand
        });
    };
    /**
     * Add edit status for virtual question
     * While adding new rule, it prevents from adding new option
     */
    setEditStatus = (status: boolean, editing?: boolean) => {
        this.setState(
            {
                enableAdd: status
            },
            () => {
                if (editing) {
                    // this.handleKeyPress();
                    this.state.autoSavingService.clearEditing();
                } else {
                    this.state.autoSavingService.setEditing();
                }
            }
        );
    };

    /**
     * @Override setQuestion function from QuestionFormBase.
     */
    setQuestion = (keyName: string, value: any) => {
        let options = [...(this.state.question.options || [])];
        let showContinueButton = this.state.question.showContinueButton;

        if (
            keyName === 'max' &&
            value > 1 &&
            this.state.question.type === QTYPES.POPUP
        ) {
            value > 1
                ? (showContinueButton = true)
                : (showContinueButton = false);
        }

        if (keyName === 'popupCardTypes') {
            this.setState(
                {
                    question: {
                        ...(this.state.question as Question),
                        popupSettings: {
                            ...this.state.question.popupSettings,
                            popupCardTypes: value
                        }
                    }
                } as Pick<
                    QuestionFormWithOptionsBaseStates,
                    keyof QuestionFormWithOptionsBaseStates
                >,
                this.setEditFlag
            );
            return;
        }

        if (keyName === 'skipFirstStage') {
            this.setState(
                {
                    question: {
                        ...(this.state.question as Question),
                        isVisible: value
                    }
                } as Pick<
                    QuestionFormWithOptionsBaseStates,
                    keyof QuestionFormWithOptionsBaseStates
                >,
                this.setEditFlag
            );
            return;
        }

        if (keyName === 'tradeoffTypes') {
            this.setState(
                {
                    question: {
                        ...(this.state.question as Question),
                        tradeoffSettings: {
                            ...this.state.question.tradeoffSettings,
                            tradeoffTypes: value
                        },
                        piping: false,
                        options: [],
                        isRichText: value === TRADE_OFF_TYPES.TEXT
                    }
                } as Pick<
                    QuestionFormWithOptionsBaseStates,
                    keyof QuestionFormWithOptionsBaseStates
                >,
                this.setEditFlag
            );
            return;
        }

        if (keyName === 'random') {
            options = options.map((o) => {
                return {
                    ...o,
                    randomizeOption: !!value
                };
            });
        }

        if (keyName === 'pipingQuestions') {
            const pipingQuestions = value.map((pipingQuestion) => {
                return {
                    id: pipingQuestion.id,
                    reversePiping: pipingQuestion.reversePiping
                };
            });
            this.setState(
                (prevState) =>
                    ({
                        question: {
                            ...prevState.question,
                            pipingQuestions
                        }
                    } as Pick<
                        QuestionFormWithOptionsBaseStates,
                        keyof QuestionFormWithOptionsBaseStates
                    >),
                this.setEditFlag
            );
            return;
        }
        this.setState(
            (prevState) =>
                ({
                    question: {
                        ...(prevState.question as Question),
                        options,
                        showContinueButton,
                        [keyName]: value
                    }
                } as Pick<
                    QuestionFormWithOptionsBaseStates,
                    keyof QuestionFormWithOptionsBaseStates
                >),
            this.setEditFlag
        );
    };

    /**
     * Update Rich text values in question state
     */
    setOtherOptionWithLanguage = (
        keyName: string,
        value: any,
        language: string
    ) => {
        const otherOption = this.state.question?.optionSettings
            ?.otherOption || {
            label: 'Other',
            texts: [],
            value: OTHER_OPTION_VALUE,
            mediaURL: ''
        };
        const currentObject = otherOption[keyName] || [];
        let uploadedValue = false;
        const newFields = currentObject.filter(
            (curr) => curr.language === language
        )[0] || {
            language,
            contents: [{ type: MULTIMEDIA_TYPES.TEXT, text: value }]
        };
        const currentLanguage = currentObject.map((curr) => {
            if (curr.language === language) {
                uploadedValue = true;
                return {
                    ...curr,
                    contents: [{ ...newFields.contents[0], text: value }]
                };
            }
            return curr;
        });
        if (!uploadedValue) {
            currentObject.push(newFields);
            this.setState(
                (prevState) => ({
                    ...prevState,
                    question: {
                        ...(prevState.question as Question),
                        optionSettings: {
                            ...(prevState.question?.optionSettings || {}),
                            otherOption: {
                                ...prevState.question?.optionSettings
                                    .otherOption,
                                [keyName]: currentObject
                            }
                        }
                    }
                }),
                this.setEditFlag
            );
            return;
        }
        this.setState(
            (prevState) => ({
                ...prevState,
                question: {
                    ...(prevState.question as Question),
                    optionSettings: {
                        ...(prevState.question?.optionSettings || {}),
                        otherOption: {
                            ...prevState.question?.optionSettings.otherOption,
                            [keyName]: [...currentLanguage]
                        }
                    }
                }
            }),
            this.setEditFlag
        );
    };

    /**
     * Saves Question after all validation is done.
     */
    addNewOption = () => {
        const qtype = this.state.question.type;
        if (
            (qtype === QTYPES.POPUP || qtype === QTYPES.VIRTUAL) &&
            (this.state.question.options || []).length == 0
        ) {
            this.onSaveOptionSettings();
        } else if (
            qtype === QTYPES.TRADE_OFF ||
            qtype === QTYPES.VIDEO_ANNOTATION
        ) {
            const languages = this.props.currentProject.languages || [];
            const buttonFields = [...(this.state.question.buttons || [])];
            const valueIndex = (this.state.question.buttons || []).length + 1;
            buttonFields.push(
                getNewOption(languages, {
                    randomizeOption: this.state.question.random,
                    value: valueIndex,
                    label: `Option Label ${valueIndex}`
                })
            );
            this.setState(
                {
                    question: {
                        ...(this.state.question as Question),
                        buttons: buttonFields
                    }
                },
                this.setEditFlag
            );
            return;
        } else if (this.state.question.options?.length == 0) {
            this.setState({
                isOptionSettingsOpen: true
            });
        } else {
            const languages = this.props.currentProject.languages || [];
            const optionFields = [...(this.state.question.options || [])];
            const valueIndex = (this.state.question.options || []).length + 1;

            if (this.state.question.type === QTYPES.VIRTUAL) {
                optionFields.push({
                    ...getNewOption(languages, {
                        randomizeOption: this.state.question.random,
                        value: valueIndex,
                        label: `Option Label ${valueIndex}`
                    }),
                    conditions: [
                        {
                            rules: [defaultRules()],
                            new: true,
                            isEdit: true,
                            conditionID: 0
                        } as Condition
                    ]
                });
            } else {
                optionFields.push(
                    getNewOption(languages, {
                        randomizeOption: this.state.question.random,
                        value: valueIndex,
                        label: `${
                            this.state.question.type === QTYPES.SCALE
                                ? 'Scale Label'
                                : 'Option Label'
                        } ${valueIndex}`
                    })
                );
            }
            this.setState(
                {
                    question: {
                        ...(this.state.question as Question),
                        options: optionFields
                    }
                },
                this.setEditFlag
            );
        }
    };

    clearOptions = () => {
        const options = this.state.question.options || [];
        const deletedOptionIDs = options
            .filter((option) => option?.id)
            .map((option) => option.id);

        this.handleOptionFields('options', [], deletedOptionIDs);
    };
    /**
     * Saves Option Settings to question object
     */
    onSaveOptionSettings = () => {
        this.setState({
            optionSettingErrors: { number: '' }
        });

        // const optionValid = checkOptionValid(this.state.question);
        // if (!optionValid.isFormValid) {
        //     this.setState({
        //         optionSettingErrors: optionValid.optionsSettingErrors
        //     });
        //     return;
        // }

        let settings: any = {
            isOptionSettingsOpen: false,
            showCardSettings: true
        };

        if ((this.state.question.options || []).length === 0) {
            if (this.state.openOpenPack) {
                return this.setState({
                    ...settings,
                    isOptionPackModalOpen: true,
                    openOpenPack: false
                });
            }
            const languages = this.props.currentProject.languages || [];
            const optionFields = [...(this.state.question.options || [])];
            if (this.state.question.type === QTYPES.VIRTUAL) {
                optionFields.push({
                    ...getNewOption(languages, {
                        randomizeOption: this.state.question.random,
                        value: 1,
                        label: `Option Label 1`
                    }),
                    conditions: [
                        {
                            rules: [defaultRules()],
                            new: true,
                            isEdit: true,
                            conditionID: 0
                        } as Condition
                    ]
                });
            } else {
                optionFields.push(
                    getNewOption(languages, {
                        randomizeOption: this.state.question.random,
                        value: 1,
                        label: `Option Label 1`
                    })
                );
            }

            settings = {
                ...settings,
                question: {
                    ...(this.state.question as Question),
                    options: optionFields
                }
            };
        }
        this.setState(settings, this.setEditFlag);
    };

    /**
     * Updates options in question state. Called by Option Component.
     */
    handleOptionFields = (keyName, data, deletedOptionIDs = []) => {
        this.setState(
            {
                question: {
                    ...(this.state.question as Question),
                    deletedOptionIDs: [
                        ...(this.state.question.deletedOptionIDs || []),
                        ...deletedOptionIDs
                    ],
                    [keyName]: [...data]
                }
            } as Pick<
                QuestionFormWithOptionsBaseStates,
                keyof QuestionFormWithOptionsBaseStates
            >,
            this.setEditFlag
        );
    };

    /** NEW */
    handleOptions = (options: OptionData[], deleteOptionIDs = []): void => {
        console.log('OPTION in handleOptions: ', options);
        this.setState(
            (prevState) => ({
                ...prevState,
                question: {
                    ...prevState.question,
                    deletedOptionIDs: [
                        ...(prevState.question.deletedOptionIDs || []),
                        ...deleteOptionIDs
                    ],
                    options,
                    optionSettings: {
                        ...prevState.question.optionSettings,
                        useOptionpack: false,
                        optionpackId: null
                    }
                }
            }),
            this.setEditFlag
        );
    };
    /**
     * Selects piping options
     */
    handlePipeOptionFields = (data) => {
        this.setState(
            {
                question: {
                    ...(this.state.question as Question),
                    pipingQuestions: [...data]
                }
            } as Pick<any, keyof QuestionFormWithOptionsBaseStates>,
            this.setEditFlag
        );
    };

    /**
     * Selects options from option pack.
     * Options will be duplicated from option pack.
     */
    onSelectOpenPack = (selectedOptionpack: IOptionPack) => {
        this.setState(
            (prevState) => ({
                question: {
                    ...(prevState.question as Question),
                    options: [
                        ...(prevState.question.options || []),
                        ...selectedOptionpack.options
                    ]
                },
                isOptionPackModalOpen: false,
                showCardSettings: true
            }),
            this.setEditFlag
        );
    };

    /**
     * Renders all option cards based on option type: Text only, Image only, Image and Text
     */
    renderOptionCard = (
        selectedOptionType = this.state.question.selectedOptionType,
        optionSettings: IOptionRederSettings = {},
        keyName = 'options'
    ) => {
        const optionError =
            (this.state.showError && this.state.questionError[keyName]) || {};
        return (
            <Option
                isRichText={
                    selectedOptionType === OPTION_TYPES.TEXT_ONLY
                        ? this.state.question.isRichText
                        : false
                }
                isTypeScale={this.state.question.type === QTYPES.SCALE}
                showRandomize={this.state.question.random}
                handleOptionFields={this.handleOptionFields}
                keyName={keyName}
                question={this.state.question}
                options={this.state.question[keyName]}
                selectedOptionType={selectedOptionType}
                optionsError={optionError}
                editEnabled={this.props.editEnabled}
                optionSettings={optionSettings}
            />
        );
    };

    /**
     * Renders Option Add / Piping option / OptionPack buttons
     */
    renderOptionAddButton = (optionLimit = false) => {
        // Limit maximum 6 options for QTYPES.POPUP
        const qtype = this.state.question.type;
        if (
            !(
                this.state.isOptionSettingsOpen &&
                (this.state.question.options || []).length === 0
            )
        ) {
            return (
                <div className="option-list">
                    <button
                        className="button button-large"
                        onClick={this.addNewOption}
                        disabled={
                            optionLimit ||
                            !this.state.enableAdd ||
                            !this.props.editEnabled
                        }
                    >
                        <i className="nx nx-add" />
                        <span className="title">Add options</span>
                    </button>
                    {this.state.question.piping && (
                        <button
                            className="button button-large"
                            onClick={this.togglePipeOptionModal}
                            disabled={optionLimit || !this.props.editEnabled}
                        >
                            <i className="nx-pipe nx"></i>
                            <span className="title">Pipe options</span>
                        </button>
                    )}
                    {![
                        QTYPES.RATE_IMAGE,
                        QTYPES.VIRTUAL,
                        QTYPES.VIDEO_ANNOTATION
                    ].includes(qtype as QTYPES) && (
                        <button
                            className="button button-large"
                            onClick={this.toggleOptionPackModal}
                            disabled={optionLimit || !this.props.editEnabled}
                        >
                            <i className="nx nx-option-pack"></i>
                            <span className="title">
                                Select from option pack
                            </span>
                        </button>
                    )}
                </div>
            );
        }
        return null;
    };

    getProjectHealthOptionIds = () => {
        const { projectHealth, currentQuestionId } = this.props;
        let projectHealthOptionIds = [];
        try {
            projectHealthOptionIds = (
                projectHealth[currentQuestionId]?.list || []
            )
                .filter((health) => health?.optionRefs?.length)
                .reduce((arr, item) => {
                    if (item.optionRefs.length) {
                        item.optionRefs.forEach((optionId) => {
                            !arr.includes(optionId) && arr.push(optionId);
                        });
                    }
                    return arr;
                }, []);
        } catch (err) {}
        return projectHealthOptionIds;
    };
}
